import cx from "classnames";
import { useCallback } from "react";

import ListBoxInfoWrapper from "components/listBox/InfoWrapper";
import ListBoxItem from "components/listBox/Item";
import { Maybe, PullRequest, VcsProvider } from "types/generated";
import { ChevronDown, Clock, GitBranch } from "components/icons/generated";
import UserLink from "components/UserLink";
import { generateBranchUrl } from "utils/urls";
import ListBox from "components/listBox";
import { useToggle } from "hooks/useToggle";
import { getCommitLabel } from "utils/commit";
import Icon from "ds/components/Icon";
import Timestamp from "components/Timestamp";

import RunItem from "./RunItem";
import styles from "./styles.module.css";

type PullRequestProps = {
  moduleApiHost: Maybe<string>;
  moduleId: string;
  moduleRepository: string;
  moduleRepositoryURL: string | null;
  moduleNamespace: string;
  provider: VcsProvider;
  pullRequest: PullRequest;
};

const PullRequestComponent = (props: PullRequestProps) => {
  const {
    moduleId,
    moduleApiHost,
    moduleNamespace,
    moduleRepository,
    provider,
    pullRequest,
    moduleRepositoryURL,
  } = props;

  const [isCollapsed, toggle] = useToggle(false);

  const hasRuns = pullRequest.runs.length > 0;
  const handleToggle = useCallback(() => toggle(), [toggle]);

  return (
    <div className={styles.prListItemWrapper}>
      <ListBox
        headerLink={{
          name: `${pullRequest.title} #${pullRequest.id}`,
          to: pullRequest.url,
        }}
        className={styles.prListItem}
        compact
      >
        {hasRuns && (
          <button
            className={cx(styles.prListItemToggle, {
              [styles.prListItemToggleCollapsed]: isCollapsed,
            })}
            onClick={handleToggle}
          >
            <Icon src={ChevronDown} />
          </button>
        )}

        <ListBoxInfoWrapper>
          <ListBoxItem icon={Clock}>
            Opened on <Timestamp timestamp={pullRequest.createdAt} /> by{" "}
            <UserLink login={pullRequest.authorLogin} name="" provider={provider} />
          </ListBoxItem>
          <ListBoxItem icon={Clock}>
            Updated on <Timestamp timestamp={pullRequest.updatedAt} />
          </ListBoxItem>

          {moduleApiHost && (
            <ListBoxItem
              link={{
                url: generateBranchUrl({
                  apiHost: moduleApiHost || "",
                  namespace: moduleNamespace,
                  repository: moduleRepository,
                  repositoryURL: moduleRepositoryURL,
                  provider: provider,
                  branch: pullRequest.headBranch,
                }),
                text: pullRequest.headBranch,
              }}
              icon={GitBranch}
            />
          )}
          <ListBoxItem
            link={{
              url: pullRequest.headSHA,
              text: getCommitLabel(provider, pullRequest.headSHA),
            }}
          />
        </ListBoxInfoWrapper>
      </ListBox>
      {hasRuns && (
        <div
          className={cx(styles.prListRunsWrapper, {
            [styles.prListRunsWrapperHidden]: isCollapsed,
          })}
        >
          {pullRequest.runs.map((run) => (
            <RunItem
              key={run.id}
              run={run}
              moduleId={moduleId}
              pullRequestUrl={pullRequest.url}
              pullRequestTitle={pullRequest.title}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PullRequestComponent;
